import { useEffect, useCallback, useRef } from 'react';
import { useCanvas } from './useCanvas';
import { useHistory } from './useHistory';
import useStore from '../store/useStore'; // Import the Zustand store

type Modifier = "ctrl" | "shift" | "alt" | "meta";
type Key = string;

interface Hotkey {
  key: Key;
  modifiers?: Modifier[];
  description: string;
  handler: (e: KeyboardEvent) => void;
  preventDefault?: boolean;
}

interface UseHotkeysReturn {
  registerHotkey: (hotkey: Hotkey) => void;
  unregisterHotkey: (key: Key) => void;
}

export function useHotkeys(initialHotkeys: Hotkey[] = []): UseHotkeysReturn {
  const { undo, redo } = useHistory();
  const { selectAll, clearSelection } = useCanvas();
  const { togglePanels } = useStore(); // Get the togglePanels action
  const hotkeyMapRef = useRef(new Map<string, Hotkey>());

  const defaultHotkeys: Hotkey[] = [
    {
      key: "z",
      modifiers: ["meta"], // Cmd+Z for Mac Undo
      description: "Undo",
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        undo();
      },
      preventDefault: true,
    },
    {
      key: "z",
      modifiers: ["ctrl"], // Ctrl+Z for Windows/Linux Undo
      description: "Undo",
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        undo();
      },
      preventDefault: true,
    },
    {
      key: "z",
      modifiers: ["meta", "shift"], // Cmd+Shift+Z for Mac Redo
      description: "Redo",
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        redo();
      },
      preventDefault: true,
    },
    {
      key: "y",
      modifiers: ["ctrl"], // Ctrl+Y for Windows/Linux Redo
      description: "Redo",
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        redo();
      },
      preventDefault: true,
    },
    {
      key: "a",
      modifiers: ["ctrl", "meta"],
      description: "Select All",
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        selectAll();
      },
      preventDefault: true,
    },
    {
      key: "Escape",
      description: "Clear Selection",
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        clearSelection();
      },
      preventDefault: true,
    },
    {
      key: '/',
      description: 'Toggle Side Panels',
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        togglePanels();
      },
      preventDefault: true,
    },
  ];

  const getHotkeyString = useCallback((hotkey: Hotkey): string => {
    // Special case for Cmd+K / Ctrl+K to ensure cross-platform compatibility
    if (hotkey.key.toLowerCase() === "k" && 
        ((hotkey.modifiers?.includes("meta")) || 
         (hotkey.modifiers?.includes("ctrl")))) {
      return "k+meta+ctrl";
    }
    
    const modifiers = hotkey.modifiers || [];
    return [...modifiers, hotkey.key.toLowerCase()].join("+");
  }, []);

  const isModifierPressed = useCallback(
    (event: KeyboardEvent, modifier: Modifier): boolean => {
      switch (modifier) {
        case "ctrl":
          return event.ctrlKey;
        case "shift":
          return event.shiftKey;
        case "alt":
          return event.altKey;
        case "meta":
          return event.metaKey;
        default:
          return false;
      }
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // console.log('[useHotkeys] KeyDown Event:', event.key, 'Ctrl:', event.ctrlKey, 'Meta:', event.metaKey, 'Target:', event.target); // Remove previous log

      // Get the key and check if it's a navigation key that we want to allow in search inputs
      let keyLower = event.key.toLowerCase();
      const isNavigationKey = keyLower === 'arrowup' || keyLower === 'arrowdown' || keyLower === 'enter' || keyLower === 'escape';
      const isSearchInput = event.target instanceof HTMLInputElement && 
                           (event.target.placeholder === 'Search...' || 
                            event.target.classList.contains('search-input'));
      
      // Skip hotkey handling if we're in an input field, but allow navigation keys in search inputs
      if (
        (event.target instanceof HTMLInputElement ||
         event.target instanceof HTMLTextAreaElement ||
         (event.target instanceof HTMLElement && event.target.isContentEditable)) &&
        !(isNavigationKey && isSearchInput)
      ) {
        return;
      }


      // Special case for Enter key - only when canvas is in focus (not in form fields)
      if (keyLower === "enter") {
        
        // Check if we're not in any input field
        const isInFormField =
          event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLTextAreaElement ||
          (event.target instanceof HTMLElement && event.target.isContentEditable);
        
        // Check if we're in the canvas area
        const isCanvasInFocus = 
          event.target instanceof HTMLElement && 
          (
            // Check for specific canvas elements
            event.target.closest('.reactflow-wrapper') || 
            event.target.closest('.react-flow') ||
            event.target.classList.contains('react-flow__pane') ||
            // Also consider the case where body is the target but no dialogs are open
            (event.target.tagName === 'BODY' && 
             // Check if any dialogs or menus are open
             !document.querySelector('.dialog-open') && 
             !document.querySelector('.menu-open') &&
             // Check if we're on the canvas page
             window.location.pathname.includes('canvas'))
          );
        
        // Only trigger Enter hotkey when canvas is in focus and not in a form field
        if (!isInFormField && isCanvasInFocus) {
          
          const enterHotkey = Array.from(hotkeyMapRef.current.entries())
            .find(([hotkeyString, _]) => hotkeyString === "enter");
          
          if (enterHotkey) {
            const [_, hotkey] = enterHotkey;
            if (hotkey.preventDefault) {
              event.preventDefault();
            }
            hotkey.handler(event);
            return;
          }
          else {
          }
        }
      }

      // Check each registered hotkey
      for (const [hotkeyString, hotkey] of hotkeyMapRef.current.entries()) {
        // Skip the special case we already handled
        if (hotkeyString === "k+meta+ctrl") continue;
        
        const [lastKey, ...modifiers] = hotkeyString.split("+").reverse();

        // Check if the pressed key matches
        if (keyLower !== lastKey) continue;

        // console.log(`[useHotkeys] Key match: ${keyLower}. Checking modifiers: ${modifiers.join('+')}`); // Remove previous log

        // --- Revised Modifier Check ---
        // Separate primary (Ctrl/Meta) and other modifiers
        const primaryModifiersRequired = modifiers.filter(mod => mod === 'ctrl' || mod === 'meta');
        const otherModifiersRequired = modifiers.filter(mod => mod !== 'ctrl' && mod !== 'meta');

        // Check if all *other* required modifiers are pressed
        const otherModifiersPressed = otherModifiersRequired.every(mod => isModifierPressed(event, mod as Modifier));

        // Check if the required *primary* modifier logic is met
        let primaryModifierPressed = false;
        if (primaryModifiersRequired.length > 0) {
            // If Ctrl or Meta is required by the hotkey definition, check if at least one is pressed
            primaryModifierPressed = isModifierPressed(event, 'ctrl') || isModifierPressed(event, 'meta');
        } else {
            // If neither Ctrl nor Meta is required by the definition, they must NOT be pressed for a match
            primaryModifierPressed = !isModifierPressed(event, 'ctrl') && !isModifierPressed(event, 'meta');
        }

        // Check if *only* the required modifiers (and the key) are active
        // This prevents Shift+Ctrl+C from triggering a Ctrl+C hotkey.
        const allPressedModifiers: Modifier[] = ['ctrl', 'meta', 'shift', 'alt'].filter(mod => isModifierPressed(event, mod as Modifier)) as Modifier[];
        const requiredModifierSet = new Set(modifiers);
        // Allow pressing *either* Ctrl or Meta if *both* are in the definition (like for copy/paste)
        const effectivePressedModifiers = new Set(allPressedModifiers);
        if (requiredModifierSet.has('ctrl') && requiredModifierSet.has('meta')) {
            if (effectivePressedModifiers.has('ctrl')) effectivePressedModifiers.add('meta');
            if (effectivePressedModifiers.has('meta')) effectivePressedModifiers.add('ctrl');
        }
        const exactModifiersPressed = effectivePressedModifiers.size === requiredModifierSet.size &&
                                      Array.from(effectivePressedModifiers).every(mod => requiredModifierSet.has(mod));


        // Final check: Are all required 'other' modifiers pressed? AND Is the primary modifier logic met? AND Are the exact modifiers pressed?
        if (otherModifiersPressed && primaryModifierPressed && exactModifiersPressed) {
        // --- End Revised Modifier Check ---

        // if (allModifiersPressed) { // Old check
          // console.log(`[useHotkeys] Modifiers match for ${keyLower}. Firing handler.`); // Remove previous log
          if (hotkey.preventDefault) {
            event.preventDefault();
          }
          hotkey.handler(event);
          break;
        } else {
           console.log(`[useHotkeys] Modifiers mismatch for ${keyLower}. Required: ${modifiers.join('+')}. Pressed: Ctrl=${event.ctrlKey}, Meta=${event.metaKey}, Shift=${event.shiftKey}, Alt=${event.altKey}`); // Log mismatch
        }
      }
    },
    [isModifierPressed, getHotkeyString, togglePanels], // Added togglePanels dependency
  );

  useEffect(() => {
    // Register default hotkeys
    [...defaultHotkeys, ...initialHotkeys].forEach((hotkey) => {
      if (hotkey.key === "Enter") {
      }
      const hotkeyString = getHotkeyString(hotkey);
      hotkeyMapRef.current.set(hotkeyString, hotkey);
    });

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown, getHotkeyString, initialHotkeys]);

  const registerHotkey = useCallback(
    (hotkey: Hotkey) => {
      const hotkeyString = getHotkeyString(hotkey);
      hotkeyMapRef.current.set(hotkeyString, hotkey);
    },
    [getHotkeyString],
  );

  const unregisterHotkey = useCallback((key: Key) => {
    // We need to handle the case where the key might have modifiers
    // For simplicity, we'll just try to find and remove any hotkey that ends with this key
    const keyLower = key.toLowerCase();
    
    // Find all hotkeys that end with this key
    const keysToRemove: string[] = [];
    hotkeyMapRef.current.forEach((_, hotkeyString) => {
      const parts = hotkeyString.split('+');
      const lastPart = parts[parts.length - 1];
      if (lastPart === keyLower) {
        keysToRemove.push(hotkeyString);
      }
    });
    
    // Remove all matching hotkeys
    keysToRemove.forEach(k => hotkeyMapRef.current.delete(k));
  }, []);

  return {
    registerHotkey,
    unregisterHotkey,
  };
}
